// src/components/subscription/SubscriptionList.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Alert, Button, Badge, Spinner, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getAuthHeaders } from '../../utils/authUtils';
import { 
  faArrowLeft, 
  faCalendarAlt, 
  faCreditCard, 
  faCheckCircle, 
  faExclamationTriangle,
  faTimesCircle,
  faClock,
  faEllipsisV,
  faEdit,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';

const SubscriptionList = ({ user }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.id) {
      fetchUserSubscriptions();
    }
  }, [user]);

  const fetchUserSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/payments/subscriptions/user/${user.id}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch subscriptions');
      }

      const data = await response.json();
      setSubscriptions(data);
      setError('');
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      setError(err.message || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { variant: 'success', icon: faCheckCircle, text: 'Active' },
      trialing: { variant: 'info', icon: faClock, text: 'Trial' },
      canceled: { variant: 'secondary', icon: faTimesCircle, text: 'Canceled' },
      past_due: { variant: 'warning', icon: faExclamationTriangle, text: 'Past Due' },
      incomplete: { variant: 'warning', icon: faExclamationTriangle, text: 'Incomplete' },
      unpaid: { variant: 'danger', icon: faExclamationTriangle, text: 'Unpaid' }
    };

    const config = statusConfig[status] || { variant: 'secondary', icon: faTimesCircle, text: status };
    
    return (
      <Badge bg={config.variant} className="d-flex align-items-center">
        <FontAwesomeIcon icon={config.icon} className="me-1" size="sm" />
        {config.text}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const handleCancelSubscription = async (subscription) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/payments/subscriptions/${subscription.stripeSubscriptionId}/cancel`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }

      // Refresh the subscriptions list
      await fetchUserSubscriptions();
      setShowCancelModal(false);
      setSubscriptionToCancel(null);
    } catch (err) {
      console.error('Error canceling subscription:', err);
      setError(err.message || 'Failed to cancel subscription');
    }
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading your subscriptions...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h2 className="mb-0">My Subscriptions</h2>
              <p className="text-muted mb-0">Manage your Sked Guy subscriptions</p>
            </div>
            <Button 
              variant="outline-primary" 
              onClick={() => navigate('/pricing')}
            >
              View All Plans
            </Button>
          </div>
        </Col>
      </Row>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Subscriptions List */}
      {subscriptions.length === 0 ? (
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="text-center py-5">
              <Card.Body>
                <FontAwesomeIcon icon={faCreditCard} size="3x" className="text-muted mb-3" />
                <h4>No Subscriptions Found</h4>
                <p className="text-muted">You don't have any active subscriptions yet.</p>
                <Button variant="primary" onClick={() => navigate('/pricing')}>
                  Browse Plans
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        <Row>
          {subscriptions.map((subscription) => {
            const daysRemaining = getDaysRemaining(subscription.currentPeriodEnd);
            const isActive = subscription.status === 'active' || subscription.status === 'trialing';
            
            return (
              <Col key={subscription.id} md={6} lg={4} className="mb-4">
                <Card className={`h-100 ${isActive ? 'border-success' : ''}`}>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">{subscription.planName || 'Unknown Plan'}</h6>
                    {getStatusBadge(subscription.status)}
                  </Card.Header>
                  
                  <Card.Body>
                    {/* Plan Details */}
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Amount:</span>
                        <strong>
                          {formatAmount(subscription.amount, subscription.currency)}/
                          {subscription.billingInterval === 'MONTHLY' ? 'month' : 'year'}
                        </strong>
                      </div>
                      
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Started:</span>
                        <span>{formatDate(subscription.currentPeriodStart)}</span>
                      </div>
                      
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">
                          {subscription.status === 'canceled' ? 'Ends:' : 'Renews:'}
                        </span>
                        <span>{formatDate(subscription.currentPeriodEnd)}</span>
                      </div>

                      {/* Days Remaining */}
                      {isActive && daysRemaining !== null && (
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Days remaining:</span>
                          <Badge bg={daysRemaining <= 7 ? 'warning' : 'info'}>
                            {daysRemaining > 0 ? daysRemaining : 'Expired'}
                          </Badge>
                        </div>
                      )}
                    </div>

                    {/* Trial Information */}
                    {subscription.status === 'trialing' && subscription.trialEnd && (
                      <Alert variant="info" className="py-2 px-3 mb-3">
                        <small>
                          <FontAwesomeIcon icon={faClock} className="me-1" />
                          Trial ends on {formatDate(subscription.trialEnd)}
                        </small>
                      </Alert>
                    )}

                    {/* Action Buttons */}
                    <div className="d-grid gap-2">
                      {isActive && !subscription.cancelAtPeriodEnd && (
                        <>
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            onClick={() => navigate('/pricing')}
                          >
                            <FontAwesomeIcon icon={faEdit} className="me-1" />
                            Change Plan
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => {
                              setSubscriptionToCancel(subscription);
                              setShowCancelModal(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} className="me-1" />
                            Cancel Subscription
                          </Button>
                        </>
                      )}

                      {isActive && subscription.cancelAtPeriodEnd && (
                        <Button 
                          variant="primary" 
                          size="sm"
                          onClick={() => navigate('/pricing')}
                        >
                          Renew Subscription
                        </Button>
                      )}

                      {subscription.status === 'canceled' && (
                        <Button 
                          variant="primary" 
                          size="sm"
                          onClick={() => navigate('/pricing')}
                        >
                          Resubscribe
                        </Button>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {/* Cancel Confirmation Modal */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Subscription</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {subscriptionToCancel && (
            <>
              <p>Are you sure you want to cancel your <strong>{subscriptionToCancel.planName}</strong> subscription?</p>
              <Alert variant="warning">
                <small>
                  Your subscription will remain active until {formatDate(subscriptionToCancel.currentPeriodEnd)}.
                  After that, you'll lose access to premium features.
                </small>
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
            Keep Subscription
          </Button>
          <Button 
            variant="danger" 
            onClick={() => handleCancelSubscription(subscriptionToCancel)}
          >
            Cancel Subscription
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SubscriptionList;